import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Book, Clock, Calendar, CheckSquare, AlertCircle, PlusCircle, Layout, Zap, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ScheduleBanner from './ScheduleBanner';
import { useAuth, RedirectToSignIn, useUser } from '@clerk/clerk-react';
import { useAuthenticatedFetch } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import Loader from './Loader';

// Add a simple pull-to-refresh component
const PullToRefresh = ({ onRefresh, isRefreshing, isMobile }) => {
  const [startY, setStartY] = useState(0);
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const maxPullDistance = 80;
  
  const handleTouchStart = (e) => {
    if (window.scrollY === 0) {
      setStartY(e.touches[0].clientY);
      setPulling(true);
    }
  };
  
  const handleTouchMove = (e) => {
    if (!pulling) return;
    
    const y = e.touches[0].clientY;
    const distance = y - startY;
    
    if (distance > 0 && distance <= maxPullDistance) {
      setPullDistance(distance);
    }
  };
  
  const handleTouchEnd = () => {
    if (pullDistance > maxPullDistance / 2) {
      // Trigger refresh
      onRefresh();
    }
    
    setPulling(false);
    setPullDistance(0);
  };
  
  return (
    <div 
      className={`sticky top-0 z-50 w-full overflow-hidden transition-all duration-300 ${isMobile ? 'flex' : 'hidden'}`}
      style={{ height: isRefreshing ? '40px' : (pulling ? `${pullDistance}px` : '0px') }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="w-full flex items-center justify-center">
        {isRefreshing ? (
          <div className="animate-spin h-5 w-5 border-2 border-primary rounded-full border-t-transparent"></div>
        ) : (
          <div className="text-xs text-accent">
            {pullDistance > maxPullDistance / 2 ? 'Release to refresh' : 'Pull down to refresh'}
          </div>
        )}
      </div>
    </div>
  );
};

const parseScheduleString = (schedule) => {
  if (!schedule) return null;
  try {
    // Parse the schedule JSON if it's in JSON format
    const scheduleData = typeof schedule === 'string' ? JSON.parse(schedule) : schedule;
    const scheduleString = scheduleData.raw || scheduleData;

    const [days, ...timeRangeParts] = scheduleString.split(' ');
    const timeRange = timeRangeParts.join(' '); // Rejoin in case there were extra spaces
    const [startTime, endTime] = timeRange.split('-');
    
    if (!days || !startTime || !endTime) return null;

    return {
      days: days.split('').map(day => {
        switch(day) {
          case 'S': return 'Sunday';
          case 'M': return 'Monday';
          case 'T': return 'Tuesday';
          case 'W': return 'Wednesday';
          case 'R': return 'Thursday';
          case 'F': return 'Friday';
          case 'SA': return 'Saturday';
          default: return null;
        }
      }).filter(Boolean),
      startTime: startTime.trim(),
      endTime: endTime.trim()
    };
  } catch (error) {
    console.error('Error parsing schedule:', schedule, error);
    return null;
  }
};

const TypewriterText = ({ text }) => {
  const [displayText, setDisplayText] = useState('');
  
  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= text.length) {
        setDisplayText(text.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [text]);

  return (
    <span className="inline-block min-w-[1ch]">
      {displayText}
      {displayText.length < text.length && (
        <span className="animate-pulse">|</span>
      )}
    </span>
  );
};

const getCurrentSemesterInfo = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = date.getMonth();

  let semester;
  if (month >= 0 && month <= 4) {
    semester = 'Spring';
  } else if (month >= 5 && month <= 7) {
    semester = 'Summer';
  } else {
    semester = 'Fall';
  }

  return { semester, year };
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn, user } = useUser();
  const authenticatedFetch = useAuthenticatedFetch();
  const { theme } = useTheme();
  
  // Debug logs
  console.log('Auth State:', { isLoaded, isSignedIn });
  console.log('User Object:', user);
  console.log('User Name:', user?.firstName);
  console.log('Full User Data:', JSON.stringify(user, null, 2));

  const [classes, setClasses] = useState([]);
  const [lectureCount, setLectureCount] = useState(0);
  const [recentLectures, setRecentLectures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Add selected day state for mobile view
  const [selectedDay, setSelectedDay] = useState('Monday');
  // Determine if screen is mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // Add refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Modify greeting to use Clerk user data
  const getGreeting = () => {
    const hour = new Date().getHours();
    console.log('Current User:', user);
    console.log('First Name:', user?.firstName);
    const firstName = user?.firstName || 'there';
    console.log('Used First Name:', firstName);
    
    const morningGreetings = [`Good Morning, ${firstName}!`, `Hello, ${firstName}!`, `Hi there, ${firstName}!`];
    const afternoonGreetings = [`Good Afternoon, ${firstName}!`, `Hey, ${firstName}!`, `Hi, ${firstName}!`];
    const eveningGreetings = [`Good Evening, ${firstName}`, `Hello, ${firstName}`, `Hi, ${firstName}`];
    const lateNightGreetings = [`Working Late, ${firstName}?`, `Hello Night Owl`, `Still Up, ${firstName}?`];
    
    const getRandomGreeting = (greetings) => {
      return greetings[Math.floor(Math.random() * greetings.length)];
    };

    if (hour >= 4 && hour < 12) {
      return getRandomGreeting(morningGreetings);
    } else if (hour >= 12 && hour < 17) {
      return getRandomGreeting(afternoonGreetings);
    } else if (hour >= 17 && hour < 24) {
      return getRandomGreeting(eveningGreetings);
    } else {
      return getRandomGreeting(lateNightGreetings);
    }
  };

  const [greeting] = useState(() => getGreeting());

  // Add refresh function
  const handleRefresh = async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    
    try {
      // Get current semester info
      const { semester, year } = getCurrentSemesterInfo();
      
      // Fetch classes with semester filter
      const queryParams = new URLSearchParams({
        semester,
        year
      }).toString();
      
      const classesResponse = await authenticatedFetch(
        `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/classes?${queryParams}`
      );
      const classesData = await classesResponse.json();
      setClasses(classesData);

      // Fetch lecture count
      const countResponse = await authenticatedFetch(
        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/lectures/count'
      );
      const countData = await countResponse.json();
      setLectureCount(parseInt(countData.count));

      // Fetch recent lectures
      const lecturesResponse = await authenticatedFetch(
        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/lectures/recent'
      );
      const lecturesData = await lecturesResponse.json();
      setRecentLectures(lecturesData);

      setError(null);
    } catch (error) {
      console.error('Error refreshing dashboard data:', error);
      setError('Failed to refresh dashboard data');
    } finally {
      // Add a small delay to make the refresh animation visible
      setTimeout(() => setIsRefreshing(false), 800);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchDashboardData = async () => {
      if (!isLoaded || !isSignedIn) return;
      
      try {
        setIsLoading(true);
        
        // Get current semester info
        const { semester, year } = getCurrentSemesterInfo();
        
        // Fetch classes with semester filter
        const queryParams = new URLSearchParams({
          semester,
          year
        }).toString();
        
        const classesResponse = await authenticatedFetch(
          `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/classes?${queryParams}`
        );
        const classesData = await classesResponse.json();
        
        if (isMounted) {
          setClasses(classesData);
        }

        // Fetch lecture count
        const countResponse = await authenticatedFetch(
          'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/lectures/count'
        );
        const countData = await countResponse.json();
        
        if (isMounted) {
          setLectureCount(parseInt(countData.count));
        }

        // Fetch recent lectures
        const lecturesResponse = await authenticatedFetch(
          'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/lectures/recent'
        );
        const lecturesData = await lecturesResponse.json();
        
        if (isMounted) {
          setRecentLectures(lecturesData);
        }

        setError(null);
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching dashboard data:', error);
          setError('Failed to load dashboard data');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchDashboardData();

    return () => {
      isMounted = false;
    };
  }, [isLoaded, isSignedIn]);

  // Add window resize listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!isLoaded) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  // Calculate class distribution data using the schedule string from database
  const classDistributionData = [
    { name: 'Monday', classes: 0 },
    { name: 'Tuesday', classes: 0 },
    { name: 'Wednesday', classes: 0 },
    { name: 'Thursday', classes: 0 },
    { name: 'Friday', classes: 0 },
  ];

  classes.forEach(cls => {
    if (cls.schedule) {
      // No need to parse JSON, directly use the schedule string
      const days = cls.schedule.split(' ')[0];
      if (days.includes('M')) classDistributionData[0].classes++;
      if (days.includes('T')) classDistributionData[1].classes++;
      if (days.includes('W')) classDistributionData[2].classes++;
      if (days.includes('R')) classDistributionData[3].classes++;
      if (days.includes('F')) classDistributionData[4].classes++;
    }
  });

  // Calculate upcoming assignments
  const upcomingAssignments = classes.reduce((acc, cls) => {
    if (cls.assignments) {
      acc.push(...cls.assignments.map(assignment => ({
        ...assignment,
        class: cls.title
      })));
    }
    return acc;
  }, []).sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate)).slice(0, 3);

  // Calculate recent activity
  const recentActivity = classes.reduce((acc, cls) => {
    if (cls.lectures) {
      acc.push(...cls.lectures.map(lecture => ({
        action: 'Attended lecture',
        class: cls.title,
        date: lecture.date
      })));
    }
    return acc;
  }, []).sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 3);

  // Helper function to convert time to 24-hour format
  const convertTo24Hour = (time) => {
    const [hour, minutePart] = time.split(':');
    const [minutes, period] = minutePart.split(' ');
    let hour24 = parseInt(hour);
    if (period === 'PM' && hour !== '12') hour24 += 12;
    if (period === 'AM' && hour === '12') hour24 = 0;
    return { hour: hour24, minutes: parseInt(minutes) };
  };

  // Calculate the earliest and latest class times
  let earliestHour = 24;
  let latestHour = 0;

  classes.forEach(cls => {
    const schedule = parseScheduleString(cls.schedule);
    if (schedule) {
      const { hour: startHour } = convertTo24Hour(schedule.startTime);
      const { hour: endHour } = convertTo24Hour(schedule.endTime);
      if (startHour < earliestHour) earliestHour = startHour;
      if (endHour > latestHour) latestHour = endHour;
    }
  });

  // Adjust the range to start an hour before and end an hour after
  earliestHour = Math.max(earliestHour - 1, 0);
  latestHour = Math.min(latestHour + 1, 23);

  return (
    <div className="min-h-screen bg-theme theme-transition">
      {/* Pull to refresh */}
      <PullToRefresh onRefresh={handleRefresh} isRefreshing={isRefreshing} isMobile={isMobile} />
      
      {/* ScheduleBanner at the top */}
      <ScheduleBanner 
        classes={classes} 
        onNavigate={(classId) => navigate(`/class/${classId}/lectures`)}
        isMobile={isMobile}
      />

      {/* Header */}
      <header className="bg-theme border-b border-theme theme-transition">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-0 mb-6">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-theme">
                <TypewriterText text={`${greeting}`} />
              </h1>
              <p className="text-sm text-accent">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
            <button
              onClick={() => navigate('/add')}
              className={`px-4 py-2 rounded-lg flex items-center justify-center sm:justify-start gap-2 bg-primary hover:bg-primary-hover text-theme transform transition-all duration-200 hover:scale-[1.02] ${isMobile ? 'hidden' : 'w-full sm:w-auto'}`}
            >
              <PlusCircle size={18} />
              <span>Add New Class</span>
            </button>
          </div>
        </div>
      </header>

      

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="p-3 sm:p-4 rounded-lg flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 mb-4 sm:mb-6 bg-secondary border-theme">
          <Layout size={20} className="text-theme mt-1" />
          <div>
            <h3 className="font-medium mb-1 text-theme">Overview</h3>
            <p className="text-sm text-accent">
              Quick overview of your academic progress and upcoming tasks.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left Column: Calendar */}
          <div className="lg:col-span-2">
            {isMobile ? (
              <CollapsibleSection
                title="Weekly Schedule"
                icon={<Calendar />}
                defaultExpanded={true}
              >
                {/* Mobile Day Selector */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-sm font-medium text-theme">Select Day:</h3>
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map((day) => (
                      <button
                        key={day}
                        onClick={() => setSelectedDay(day)}
                        className={`py-2 px-1 text-xs font-medium rounded-md transition-all ${
                          selectedDay === day 
                            ? 'bg-primary text-theme' 
                            : 'bg-secondary hover:bg-theme text-theme'
                        }`}
                      >
                        {day.substring(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Mobile Schedule (single day view) */}
                <div className="mt-2">
                  <div className="grid grid-cols-1 gap-1">
                    {/* Time Column */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between pr-2 mb-2">
                        <div className="font-medium text-theme">{selectedDay}</div>
                      </div>
                      
                      {Array.from({ length: latestHour - earliestHour + 1 }, (_, i) => i + earliestHour).map((hour) => {
                        const timeLabel = hour > 12 ? `${hour-12}:00 PM` : `${hour}:00 AM`;
                        const classesAtTime = classes.filter(cls => {
                          if (!cls || !cls.schedule) return false;
                          const schedule = parseScheduleString(cls.schedule);
                          if (!schedule || !schedule.days.includes(selectedDay)) return false;
                          
                          try {
                            const { hour: startHour } = convertTo24Hour(schedule.startTime);
                            const { hour: endHour } = convertTo24Hour(schedule.endTime);
                            return hour >= startHour && hour < endHour;
                          } catch (error) {
                            return false;
                          }
                        });
                        
                        return (
                          <div key={hour} className="mb-2">
                            <div className="flex items-center text-xs border-b border-theme border-opacity-10 pb-1 text-accent">
                              {timeLabel}
                            </div>
                            {classesAtTime.length > 0 ? (
                              <div className="mt-1 space-y-1">
                                {classesAtTime.map(cls => {
                                  const schedule = parseScheduleString(cls.schedule);
                                  return (
                                    <div 
                                      key={`${cls.title}-${hour}`}
                                      className="py-1 px-2 rounded-md bg-primary border border-theme"
                                    >
                                      <div className="font-medium text-xs text-theme">{cls.title}</div>
                                      <div className="text-[10px] text-accent">
                                        {schedule.startTime} - {schedule.endTime}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="py-2 text-[10px] text-accent italic">No classes</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </CollapsibleSection>
            ) : (
              <DashboardSection
                title="Weekly Schedule"
                icon={<Calendar />}
              >
                <div className="overflow-x-auto -mx-2 sm:mx-0 px-2 sm:px-0">
                  <div className="min-w-[600px]">
                    <div className="grid grid-cols-6 gap-1">
                      {/* Time Column */}
                      <div className="space-y-1">
                        <div className="h-6 flex items-center justify-end pr-2 font-medium text-xs text-theme">
                          Time
                        </div>
                        {Array.from({ length: latestHour - earliestHour + 1 }, (_, i) => i + earliestHour).map((hour) => (
                          <div 
                            key={hour} 
                            className="h-12 flex items-center justify-end pr-2 text-xs text-accent"
                          >
                            {hour > 12 ? `${hour-12}:00 PM` : `${hour}:00 AM`}
                          </div>
                        ))}
                      </div>
                      
                      {/* Days Columns */}
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map((day) => (
                        <div key={day} className="space-y-1">
                          <div className="h-6 flex items-center justify-center font-medium text-xs text-theme">
                            {day.substring(0, 3)}
                          </div>
                          <div className="relative" style={{ height: `${(latestHour - earliestHour + 1) * 48}px` }}>
                            {classes.filter(cls => cls && cls.schedule).map((cls) => {
                              const schedule = parseScheduleString(cls.schedule);
                              if (!schedule || !schedule.days.includes(day)) return null;

                              try {
                                const { hour: startHour, minutes: startMinutes } = convertTo24Hour(schedule.startTime);
                                const { hour: endHour, minutes: endMinutes } = convertTo24Hour(schedule.endTime);

                                const top = (startHour - earliestHour) * 48 + (startMinutes / 60) * 48;
                                const height = (endHour - startHour) * 48 + ((endMinutes - startMinutes) / 60) * 48;

                                return (
                                  <div
                                    key={`${cls.title}-${day}`}
                                    style={{
                                      top: `${top}px`,
                                      height: `${height}px`,
                                    }}
                                    className="absolute left-0.5 right-0.5 rounded-md bg-primary p-1 text-xs overflow-hidden border border-theme transform transition-all hover:z-10 hover:scale-[1.02]"
                                  >
                                    <div className="font-medium truncate text-[10px] text-theme">{cls.title}</div>
                                    <div className="truncate text-[9px] text-accent">
                                      {schedule.startTime.replace(':00', '')} - {schedule.endTime.replace(':00', '')}
                                    </div>
                                  </div>
                                );
                              } catch (error) {
                                console.error('Error rendering class schedule:', cls.title, error);
                                return null;
                              }
                            })}
                            
                            {/* Time grid lines */}
                            {Array.from({ length: latestHour - earliestHour + 1 }, (_, i) => (
                              <div 
                                key={i} 
                                className="absolute left-0 right-0 h-12 border-t border-theme opacity-10"
                                style={{ top: `${i * 48}px` }}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </DashboardSection>
            )}
          </div>

          {/* Right Column: Stats and Quick Actions */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-1 gap-4">
              <StatsCard
                title="Total Classes"
                value={classes.length}
                icon={<Book />}
                description="Active courses this semester"
              />
              <StatsCard
                title="Total Lectures"
                value={lectureCount}
                icon={<BookOpen />}
                description="Lectures recorded"
              />
            </div>
            {isMobile ? (
              <CollapsibleSection
                title="Quick Actions"
                icon={<Zap />}
              >
                <div className="space-y-2">
                  <QuickActionButton text="View Classes" icon={<Book />} onClick={() => navigate('/classes')} />
                  <QuickActionButton text="Assignments" icon={<CheckSquare />} onClick={() => navigate('/assignments')} />
                  <QuickActionButton text="Schedule" icon={<Calendar />} onClick={() => navigate('/schedule')} />
                </div>
              </CollapsibleSection>
            ) : (
              <DashboardSection
                title="Quick Actions"
                icon={<Zap />}
              >
                <div className="space-y-2">
                  <QuickActionButton text="View Classes" icon={<Book />} onClick={() => navigate('/classes')} />
                  <QuickActionButton text="Assignments" icon={<CheckSquare />} onClick={() => navigate('/assignments')} />
                  <QuickActionButton text="Schedule" icon={<Calendar />} onClick={() => navigate('/schedule')} />
                </div>
              </DashboardSection>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

// New Components
const StatsCard = ({ title, value, icon, description }) => {
  const navigate = useNavigate();
  
  const getNavigationPath = () => {
    if (title === "Total Classes") return "/classes";
    if (title === "Total Lectures") return "/lectures";
    return "#";
  };
  
  return (
    <div 
      className="p-4 sm:p-6 rounded-lg border border-theme bg-theme theme-transition active:scale-95 touch-manipulation cursor-pointer transition-all duration-200 hover:shadow-md"
      onClick={() => navigate(getNavigationPath())}
    >
      <div className="flex items-center gap-3 sm:gap-4">
        <div className="p-2 sm:p-3 rounded-lg bg-primary text-theme">
          {icon}
        </div>
        <div>
          <p className="text-sm text-accent">{title}</p>
          <p className="text-xl sm:text-2xl font-bold text-theme">{value}</p>
          <p className="text-xs text-accent">{description}</p>
        </div>
      </div>
    </div>
  );
};

const DashboardSection = ({ title, icon, children }) => (
  <div className="p-4 sm:p-6 rounded-lg border border-theme bg-theme theme-transition">
    <div className="flex items-center gap-2 mb-3 sm:mb-4">
      <div className="p-2 rounded-lg bg-primary text-theme">
        {icon}
      </div>
      <h2 className="text-base sm:text-lg font-semibold text-theme">{title}</h2>
    </div>
    {children}
  </div>
);

const AssignmentItem = ({ assignment }) => (
  <div className="p-3 sm:p-4 rounded-lg bg-secondary border-theme">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
      <div>
        <p className="font-medium text-sm sm:text-base text-theme">{assignment.title}</p>
        <p className="text-xs sm:text-sm text-accent">{assignment.class}</p>
      </div>
      <div className="px-3 py-1 rounded-full text-xs sm:text-sm bg-primary text-theme self-start sm:self-center">
        {new Date(assignment.dueDate).toLocaleDateString()}
      </div>
    </div>
  </div>
);

const ActivityItem = ({ activity }) => (
  <div className="p-3 sm:p-4 rounded-lg bg-secondary border-theme">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
      <div>
        <p className="font-medium text-sm sm:text-base text-theme">{activity.action}</p>
        <p className="text-xs sm:text-sm text-accent">{activity.class}</p>
      </div>
      <div className="px-3 py-1 rounded-full text-xs sm:text-sm bg-primary text-theme self-start sm:self-center">
        {new Date(activity.date).toLocaleDateString()}
      </div>
    </div>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="text-center py-6 sm:py-8 text-accent">
    <div className="inline-flex p-3 rounded-full bg-secondary mb-2">
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="24" 
        height="24" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      >
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
        <line x1="8" y1="12" x2="16" y2="12"></line>
      </svg>
    </div>
    <p className="text-sm sm:text-base">{message}</p>
  </div>
);

const QuickActionButton = ({ text, icon, onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-2 p-2 sm:p-3 rounded-lg bg-primary hover:bg-primary-hover text-theme transform transition-all duration-200 hover:scale-[1.02] theme-transition"
  >
    <span className="text-theme">{icon}</span>
    <span className="text-sm sm:text-base">{text}</span>
  </button>
);

// Add Collapsible Section Component for mobile
const CollapsibleSection = ({ title, icon, children, defaultExpanded = true }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  return (
    <div className="p-4 sm:p-6 rounded-lg border border-theme bg-theme theme-transition">
      <div 
        className="flex items-center justify-between gap-2 mb-3 sm:mb-4 cursor-pointer"
        onClick={() => setIsExpanded(prev => !prev)}
      >
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-primary text-theme">
            {icon}
          </div>
          <h2 className="text-base sm:text-lg font-semibold text-theme">{title}</h2>
        </div>
        <div className="text-accent">
          {isExpanded ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 15l-6-6-6 6"/>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          )}
        </div>
      </div>
      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        {children}
      </div>
    </div>
  );
};

export default Dashboard;